import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { getReportingExportedPdfsUrl } from '@futrli/shared-services';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFinished } from '@futrli/ui-components/components/sidebar/wizard/wizard-finished/wizard-finished';
import { MembersView } from '@futrli/views/MembersView';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ReportDocumentView } from '@futrli/views/views/reports/ReportDocumentView/ReportDocumentView';

import { useEmailPdfForm } from './hooks/useEmailPdfForm';
import { useRouteIds } from '../../../hooks/useRouteIds';
import { trackEvent } from '../../../core/tracker';
import { TRACKING_EVENTS } from '../../../core/constants';
import { EmailFormScreen } from './screens/email-form-screen';

export interface EmailPdfWizardContainerProps {
  onClose: () => void;
  reportId?: string;
  reportName: string;
  exportedReportId?: string;
}

type Screen = 'form' | 'sending' | 'sent';

export const EmailPdfWizardContainer = ({
  onClose,
  reportId,
  reportName,
  exportedReportId
}: EmailPdfWizardContainerProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(() => new ReportDocumentView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const membersView = useApiView(
    useMemo(() => new MembersView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();

  const {
    selectedUsers,
    setSelectedUsers,
    emailSubject,
    setEmailSubject,
    emailBody,
    setEmailBody,
    validationErrors,
    validate,
    revalidate,
    FIELD_IDS
  } = useEmailPdfForm(reportName);

  // Screen transitions
  useEffect(() => {
    if (view.state.isSendingEmail) {
      setScreen('sending');
    }
  }, [view.state.isSendingEmail]);

  useEffect(() => {
    if (view.state.emailSent) {
      trackEvent(
        isConsolidation ? TRACKING_EVENTS.CREATE_CONSOLIDATION_PDF_AND_SEND : TRACKING_EVENTS.SENT_INSTANT_PDF
      );
      setScreen('sent');
    }
  }, [view.state.emailSent, isConsolidation]);

  useEffect(() => {
    if (view.state.errorSendingEmail) {
      setError(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
      setScreen('form');
    }
  }, [view.state.errorSendingEmail, intl]);

  const handleGoToExports = useCallback(() => {
    onClose();
    navigate(getReportingExportedPdfsUrl(workspaceId, organisationId, isConsolidation));
  }, [navigate, workspaceId, organisationId, isConsolidation, onClose]);

  const handleSend = useCallback(() => {
    if (!validate()) {
      return;
    }

    setError(undefined);

    const users = selectedUsers.map(s => ({
      id: s.id,
      email_address: s.email_address
    }));

    if (exportedReportId) {
      view.emailReport({ exportedReportId, body: emailBody, subject: emailSubject, users });
    } else if (reportId) {
      view.emailReport({ reportId, reportName, body: emailBody, subject: emailSubject, users });
    }
  }, [validate, selectedUsers, exportedReportId, reportId, emailBody, emailSubject, reportName, view]);

  if (membersView.state.isLoading) {
    return <LoadingWizardScreen />;
  }

  if (screen === 'sending') {
    return (
      <LoadingWizardScreen
        text={intl.formatMessage({ id: 'predict.email_pdf_sending', defaultMessage: 'Sending report...' })}
      />
    );
  }

  if (screen === 'sent') {
    return (
      <WizardScreen
        footerMode={FooterMode.CREATED}
        onCancel={onClose}
        closeText={intl.formatMessage({ id: 'common.close', defaultMessage: 'Close' })}
        onView={handleGoToExports}
        viewText={intl.formatMessage({ id: 'reporting.go_to_exports', defaultMessage: 'Go to exports' })}
      >
        <WizardFinished
          text={intl.formatMessage({
            id: 'predict.email_pdf_sent_success',
            defaultMessage: "Report sent and added to exported PDF's tab"
          })}
        />
      </WizardScreen>
    );
  }

  return (
    <EmailFormScreen
      reportName={reportName}
      members={membersView.state.members}
      selectedUsers={selectedUsers}
      setSelectedUsers={setSelectedUsers}
      emailSubject={emailSubject}
      setEmailSubject={setEmailSubject}
      emailBody={emailBody}
      setEmailBody={setEmailBody}
      validationErrors={validationErrors}
      revalidate={revalidate}
      fieldIds={FIELD_IDS}
      error={error}
      onCancel={onClose}
      onSend={handleSend}
    />
  );
};
